import CountryFlag from "@/components/relations/flag-icon";
import type { SxProps, Theme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";

// FORMIK COMPONENTS
import { Field } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Country, read_country_list } from "../../country-utils";

type InputCountrySelectProps = {
	name: string;
	label: string;
	disabled?: boolean;
	placeholder?: string;
	filter_debtor_disabled?: boolean;
	filter_freeloader_disabled?: boolean;
	sx?: SxProps<Theme>;
	size?: "small" | "medium";
	full_width?: boolean;
	on_change: (country: Country) => void;
	country_code_list?: string[];
	fixed_list?: boolean;
};

export default function InputCountrySelect({
	name = "",
	label = "",
	disabled = false,
	placeholder = null,
	filter_debtor_disabled = false,
	filter_freeloader_disabled = false,
	sx = {},
	size = "medium",
	full_width = true,
	on_change = () => {},
	country_code_list = [],
	fixed_list = false,
}: InputCountrySelectProps) {
	const [countryLoaded, setCountryLoaded] = useState(false);
	const [countryList, setCountryList] = useState<Country[]>([]);
	const { t } = useTranslation();

	useEffect(() => {
		const fetchCountryList = async () => {
			const country_list = await read_country_list();

			setCountryLoaded(true);
			setCountryList(country_list.data);
		};

		if (!countryLoaded) {
			fetchCountryList();
		}
	}, [countryLoaded]);

	const renderOptionsHandler = () => {
		if (country_code_list.length > 0) {
			return filterByTypeDisabled().filter((country) =>
				country_code_list.includes(country.country_code),
			);
		}
		return filterByTypeDisabled();
	};

	const filterByTypeDisabled = () => {
		if (filter_debtor_disabled) {
			return countryList.filter((country) => !country.is_debtor_disabled);
		}

		if (filter_freeloader_disabled) {
			return countryList.filter((country) => !country.is_freeloader_disabled);
		}

		return countryList;
	};

	if (!countryLoaded) {
		return;
	}

	return (
		<Field id={name} name={name}>
			{({ meta, field, form }) => {
				if (!field.value) {
					field.value = "";
				}

				return (
					<FormControl sx={{ ...sx }} fullWidth={full_width}>
						<Autocomplete
							disabled={disabled}
							fullWidth={full_width}
							options={renderOptionsHandler()}
							isOptionEqualToValue={(value, option) => value === option}
							getOptionLabel={(option) => t(`country_list.${option.country_code}`)}
							size={size}
							disableClearable={fixed_list}
							autoHighlight
							defaultValue={countryList.find(
								(option) => option.country_code.toLowerCase() === field.value.toLowerCase(),
							)} // Set the selected value
							onChange={(_, country) => {
								form.setFieldValue(name, country?.country_code || null);
								on_change(country);
							}}
							renderOption={(props, option) => (
								<Box component="li" sx={{ "& > img": { flexShrink: 0, mr: 2 } }} {...props}>
									<CountryFlag width={20} height={20} country_code={option.country_code} />
									{t(`country_list.${option.country_code}`)}
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									{...field}
									fullWidth={full_width}
									error={Boolean(meta.error && meta.touched)}
									label={label}
									size={size}
									placeholder={placeholder}
									inputProps={{
										...params.inputProps,
										autoComplete: "new-password", // disable autocomplete and autofill
									}}
								/>
							)}
						/>
						{meta.error && meta.touched && (
							<FormHelperText sx={{ color: "#dc3545", mt: 0.5 }}>
								{meta.error && meta.touched && meta.error}
							</FormHelperText>
						)}
					</FormControl>
				);
			}}
		</Field>
	);
}
